import cn from 'classnames';
// Ui
import Text from 'ui3/Text/Text';
// Hooks
import { PpvTier } from 'hooks/useGetPpvTiers';
// Styles
import styles from './PriceTierSelector.module.scss';

type PriceTierSelectorProps = {
  tiers: PpvTier[];
  value: string;
  onSelect: (price: number) => void;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  disabled?: boolean;
  className?: string;
};

const PriceTierSelector = ({
  tiers,
  value,
  onSelect,
  loading = false,
  error = false,
  onRetry,
  disabled = false,
  className,
}: PriceTierSelectorProps): JSX.Element => {
  if (loading) {
    return (
      <div className={cn(styles.state, className)}>
        <Text variant="captionRegular" color="lights-medium">
          Loading price options…
        </Text>
      </div>
    );
  }

  if (error || tiers.length === 0) {
    return (
      <div className={cn(styles.state, className)}>
        <Text variant="captionRegular" color="error-default">
          Couldn&apos;t load price options
        </Text>
        {onRetry && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            <Text variant="body3Medium" color="accent-default">
              Retry
            </Text>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cn(styles.list, className)}>
      {tiers.map((tier) => {
        const isSelected = Number(value) === tier.price;

        return (
          <button
            type="button"
            key={tier.id}
            aria-pressed={isSelected}
            disabled={disabled}
            className={cn(styles.chip, {
              [styles.selected]: isSelected,
              [styles.disabled]: disabled,
            })}
            onClick={() => onSelect(tier.price)}
          >
            <Text
              variant="buttonL"
              color={isSelected ? 'background-dim' : 'lights-high'}
            >
              ${tier.price}
            </Text>
          </button>
        );
      })}
    </div>
  );
};

export default PriceTierSelector;
